import React, { useState, useEffect } from "react";
import axios from "axios";
import Select from "react-select";
import { useNavigate } from "react-router-dom";
import colors from "../Constant/colors.js";
import LoadingButton from "./LoadingButton.jsx";

const ExamDateSheet = () => {
  const [classes, setClasses] = useState([]);
  const [selectedClass, setSelectedClass] = useState(null);

  const [subjects, setSubjects] = useState([]);
  const [examDetails, setExamDetails] = useState([]);
  const [allExams, setAllExams] = useState([]);

  const [teacher, setTeacher] = useState([]);
  const [selectedSession, setSelectedSession] = useState("");
  const [teacherOptions, setTeacherOptions] = useState([]);

  const [loading, setLoading] = useState(false);

  // ======================================================
  // CO-SCHOLASTIC AREAS
  // ======================================================

  const [coScholasticAreas, setCoScholasticAreas] =
    useState([]);

  const [newCoScholasticArea, setNewCoScholasticArea] =
    useState("");

  const navigate = useNavigate();

  const label18 =
    "block text-black font-semibold text-[18px] mb-1 text-left";

  const inputStyle = {
    border: "1px solid #1E40AF",
    padding: "10px",
    borderRadius: "8px",
    width: "100%",
    fontSize: "14px",
    background: "white",
  };

  const buttonStyle = {
    background: colors.gradientPrimary,
    color: "white",
    padding: "12px 22px",
    borderRadius: "8px",
    fontWeight: "700",
    fontSize: "18px",
    border: "none",
    cursor: "pointer",
    boxShadow: "0 4px 12px rgba(0,0,0,0.2)",
  };

  // ======================================================
  // LOAD INITIAL DATA
  // ======================================================

  useEffect(() => {
    const sessionIdFromLocalStorage =
      localStorage.getItem("currentSessionId");

    console.log(
      localStorage.getItem("currentSessionId")
    );

    if (!sessionIdFromLocalStorage) {
      alert(
        "Session ID is missing. Please log in again."
      );

      navigate("/login");
      return;
    }

    setSelectedSession(
      sessionIdFromLocalStorage
    );

    // ==================================================
    // FETCH CLASSES
    // ==================================================

    axios
      .get(
        `${process.env.REACT_APP_API_BASE_URL}/api/classes/getClasses?sessionId=${sessionIdFromLocalStorage}`
      )
      .then((res) => {
        setClasses(res.data);
      })
      .catch((err) => {
        console.error(
          "Error fetching classes:",
          err
        );

        alert(
          "Error fetching classes. Please try again."
        );
      });

    // ==================================================
    // FETCH EXAMS
    // ==================================================

    axios
      .get(
        `${process.env.REACT_APP_API_BASE_URL}/api/exams/getExams/${sessionIdFromLocalStorage}`
      )
      .then((res) => setAllExams(res.data))
      .catch((err) =>
        console.error(
          "Error fetching exams:",
          err
        )
      );

    // ==================================================
    // FETCH TEACHERS
    // ==================================================

    axios
      .get(
        `${process.env.REACT_APP_API_BASE_URL}/api/teachers/getTeachers`,
        {
          params: {
            sessionId:
              sessionIdFromLocalStorage,
          },
        }
      )
      .then((response) => {
        console.log(
          "Teachers response:",
          response.data
        );

        const teachersArray = Array.isArray(
          response.data
        )
          ? response.data
          : [response.data];

        const options = teachersArray.map(
          (teacher) => ({
            value: teacher._id,
            label: `${teacher.firstName} ${teacher.lastName}`,
          })
        );

        setTeacherOptions(options);
      })
      .catch((err) =>
        console.error(err)
      );
  }, [navigate]);

  // ======================================================
  // HANDLE CLASS CHANGE
  // ======================================================

  const handleClassChange = (
    selectedOption
  ) => {
    setSelectedClass(selectedOption);

    // Reset Co-Scholastic Areas
    setCoScholasticAreas([]);
    setNewCoScholasticArea("");

    if (!selectedOption) return;

    axios
      .get(
        `${process.env.REACT_APP_API_BASE_URL}/api/subjects/getSubjectsByClassName/${selectedOption.value}`,
        {
          params: {
            sessionId: selectedSession,
          },
        }
      )
      .then((res) => {
        setSubjects(res.data);

        const initial = res.data.map(
          (sub) => ({
            subjectName:
              sub.subjectName,
            examDate: "",
            startTime: "",
            endTime: "",
            invigilator: "",
            notes: "",
            room: "",
          })
        );

        setExamDetails(initial);
      })
      .catch((err) => {
        console.error(
          "Error fetching subjects:",
          err.response
            ? err.response.data
            : err.message
        );

        alert(
          "Error fetching subjects. Please try again."
        );
      });
  };

  // ======================================================
  // UPDATE SUBJECT INPUT VALUES
  // ======================================================

  const handleInputChange = (
    index,
    field,
    value
  ) => {
    const updated = [...examDetails];

    updated[index] = {
      ...updated[index],
      [field]: value,
    };

    setExamDetails(updated);
  };

  // ======================================================
  // ADD CO-SCHOLASTIC AREA
  // ======================================================

  const handleAddCoScholasticArea = () => {
    const areaName =
      newCoScholasticArea.trim();

    if (!areaName) {
      alert(
        "Please enter Co-Scholastic Area name."
      );

      return;
    }

    // Check duplicate
    const alreadyExists =
      coScholasticAreas.some(
        (area) =>
          area.name.toLowerCase() ===
          areaName.toLowerCase()
      );

    if (alreadyExists) {
      alert(
        "This Co-Scholastic Area already exists."
      );

      return;
    }

    setCoScholasticAreas([
      ...coScholasticAreas,
      {
        name: areaName,
      },
    ]);

    setNewCoScholasticArea("");
  };

  // ======================================================
  // DELETE CO-SCHOLASTIC AREA
  // ======================================================

  const handleRemoveCoScholasticArea = (
    index
  ) => {
    const updated =
      coScholasticAreas.filter(
        (_, i) => i !== index
      );

    setCoScholasticAreas(updated);
  };

  // ======================================================
  // SUBMIT FORM
  // ======================================================

  const handleSubmit = (e) => {
    e.preventDefault();

    setLoading(true);

    const examName =
      e.target.examName.value;

    if (!examName) {
      alert(
        "Please provide an exam name."
      );

      setLoading(false);
      return;
    }

    // ==================================================
    // ONLY KEEP SUBJECTS WHERE AT LEAST
    // ONE FIELD IS FILLED
    // ==================================================

    const filledSubjects =
      examDetails.filter(
        (detail) =>
          detail.examDate ||
          detail.startTime ||
          detail.endTime ||
          detail.invigilator ||
          detail.room
      );

    if (filledSubjects.length === 0) {
      alert(
        "Please fill details for at least one subject."
      );

      setLoading(false);
      return;
    }

    // ==================================================
    // CREATE API DATA
    // ==================================================

    const data = {
      examName: examName,

      class: selectedClass.value,

      subjects: filledSubjects,

      // Dynamic Co-Scholastic Areas
      coScholasticAreas:
        coScholasticAreas,

      sessionId: selectedSession,
    };

    console.log(
      "Create Exam Data:",
      data
    );

    // ==================================================
    // CREATE EXAM
    // ==================================================

    axios
      .post(
        `${process.env.REACT_APP_API_BASE_URL}/api/exams/createExam`,
        data,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem(
              "token"
            )}`,
          },
        }
      )
      .then(() => {
        alert(
          "Exam datesheet created successfully"
        );

        setSelectedClass(null);
        setSubjects([]);
        setExamDetails([]);

        // Reset Co-Scholastic Areas
        setCoScholasticAreas([]);
        setNewCoScholasticArea("");

        e.target.reset();

        // ==================================================
        // FETCH LATEST EXAMS
        // ==================================================

        axios
          .get(
            `${process.env.REACT_APP_API_BASE_URL}/api/exams/getExams/${selectedSession}`
          )
          .then((res) =>
            setAllExams(res.data)
          );
      })
      .catch((err) => {
        console.error(
          "Error creating exam:",
          err
        );

        console.error(
          "Backend Error:",
          err.response?.data
        );

        alert(
          err.response?.data?.message ||
            "Error creating exam. Please try again."
        );
      })
      .finally(() =>
        setLoading(false)
      );
  };

  // ======================================================
  // DELETE EXAM
  // ======================================================

  const handleDelete = (id) => {
    if (
      !window.confirm(
        "Delete this exam?"
      )
    )
      return;

    axios
      .delete(
        `${process.env.REACT_APP_API_BASE_URL}/api/exams/deleteExam/${id}`,
        {
          params: {
            sessionId:
              selectedSession,
          },

          headers: {
            Authorization: `Bearer ${localStorage.getItem(
              "token"
            )}`,
          },
        }
      )
      .then(() => {
        setAllExams(
          allExams.filter(
            (exam) =>
              exam._id !== id
          )
        );
      })
      .catch((err) => {
        console.error(
          "Error deleting exam:",
          err
        );

        alert(
          "Error deleting exam. Please try again."
        );
      });
  };

  // ======================================================
  // UI
  // ======================================================

  return (
    <div className="min-h-screen p-6 bg-white text-left">
      {/* MAIN WRAPPER */}
      <div className="flex-1 w-full pl-10 pr-6 text-left">

        {/* TITLE */}
        <h2
          className="font-bold mb-10 text-left"
          style={{
            color: colors.primary,
            fontSize: "18px",
          }}
        >
          Create Exam Datesheet
        </h2>

        {/* ==================================================
            CLASS SELECTOR
        ================================================== */}

        <div className="mb-6 text-left">
          <label className={label18}>
            Select Class:
          </label>

          <Select
            options={classes.map(
              (c) => ({
                value: c.className,
                label: c.className,
              })
            )}
            onChange={
              handleClassChange
            }
            placeholder="Select class"
            styles={{
              control: (base) => ({
                ...base,
                fontSize: "14px",
                textAlign: "left",
              }),
            }}
          />
        </div>

        {/* ==================================================
            FORM
        ================================================== */}

        {selectedClass && (
          <form
            className="grid grid-cols-1 gap-6 text-left"
            onSubmit={handleSubmit}
          >

            {/* ==================================================
                EXAM NAME
            ================================================== */}

            <div>
              <label className={label18}>
                Exam Name:
              </label>

              <input
                name="examName"
                placeholder="Enter exam name"
                style={inputStyle}
                required
              />
            </div>

            {/* ==================================================
                SUBJECTS
            ================================================== */}

            {subjects.map(
              (subject, index) => (
                <div
                  key={subject._id}
                  className="p-4 rounded-lg shadow-md text-left"
                  style={{
                    background:
                      colors.grayLight,
                  }}
                >
                  <h3 className="font-semibold text-[18px] mb-3 text-black">
                    {
                      subject.subjectName
                    }
                  </h3>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-left">

                    {/* DATE */}
                    <input
                      type="date"
                      style={inputStyle}
                      value={
                        examDetails[
                          index
                        ]?.examDate ||
                        ""
                      }
                      onChange={(e) =>
                        handleInputChange(
                          index,
                          "examDate",
                          e.target.value
                        )
                      }
                    />

                    {/* START TIME */}
                    <input
                      placeholder="Start Time"
                      style={inputStyle}
                      value={
                        examDetails[
                          index
                        ]?.startTime ||
                        ""
                      }
                      onChange={(e) =>
                        handleInputChange(
                          index,
                          "startTime",
                          e.target.value
                        )
                      }
                    />

                    {/* END TIME */}
                    <input
                      placeholder="End Time"
                      style={inputStyle}
                      value={
                        examDetails[
                          index
                        ]?.endTime ||
                        ""
                      }
                      onChange={(e) =>
                        handleInputChange(
                          index,
                          "endTime",
                          e.target.value
                        )
                      }
                    />

                    {/* INVIGILATOR */}
                    <div>
                      <label className="text-[18px] font-semibold text-black mb-1 block">
                        Invigilator
                      </label>

                      <Select
                        options={
                          teacherOptions
                        }
                        value={teacherOptions.find(
                          (opt) =>
                            opt.value ===
                            examDetails[
                              index
                            ]?.invigilator
                        )}
                        onChange={(
                          opt
                        ) =>
                          handleInputChange(
                            index,
                            "invigilator",
                            opt
                              ? opt.value
                              : ""
                          )
                        }
                        styles={{
                          control: (
                            base
                          ) => ({
                            ...base,
                            fontSize:
                              "14px",
                            textAlign:
                              "left",
                          }),
                        }}
                      />
                    </div>

                    {/* ROOM */}
                    <input
                      placeholder="Room Number"
                      style={inputStyle}
                      value={
                        examDetails[
                          index
                        ]?.room ||
                        ""
                      }
                      onChange={(e) =>
                        handleInputChange(
                          index,
                          "room",
                          e.target.value
                        )
                      }
                    />

                    {/* NOTES */}
                    <textarea
                      placeholder="Notes"
                      style={{
                        ...inputStyle,
                        height: "80px",
                      }}
                      value={
                        examDetails[
                          index
                        ]?.notes ||
                        ""
                      }
                      onChange={(e) =>
                        handleInputChange(
                          index,
                          "notes",
                          e.target.value
                        )
                      }
                    ></textarea>
                  </div>
                </div>
              )
            )}

            {/* ==================================================
                CO-SCHOLASTIC AREAS
            ================================================== */}

            <div
              className="p-5 rounded-lg shadow-md text-left"
              style={{
                background:
                  colors.grayLight,
              }}
            >
              <h3
                className="font-bold text-[18px] mb-4 text-black"
              >
                Co-Scholastic Areas
              </h3>

              {/* ADD AREA INPUT */}
              <div className="flex flex-col md:flex-row gap-3 items-stretch md:items-end">

                <div className="flex-1">
                  <label className="text-[16px] font-semibold text-black mb-1 block">
                    Area Name
                  </label>

                  <input
                    type="text"
                    placeholder="e.g. Discipline"
                    style={inputStyle}
                    value={
                      newCoScholasticArea
                    }
                    onChange={(e) =>
                      setNewCoScholasticArea(
                        e.target.value
                      )
                    }
                    onKeyDown={(e) => {
                      if (
                        e.key ===
                        "Enter"
                      ) {
                        e.preventDefault();

                        handleAddCoScholasticArea();
                      }
                    }}
                  />
                </div>

                <button
                  type="button"
                  onClick={
                    handleAddCoScholasticArea
                  }
                  style={{
                    ...buttonStyle,
                    fontSize: "15px",
                    padding:
                      "10px 18px",
                    whiteSpace:
                      "nowrap",
                  }}
                >
                  + Add Area
                </button>
              </div>

              {/* ADDED AREAS */}
              {coScholasticAreas.length >
                0 && (
                <div className="mt-5">
                  <p className="text-[15px] font-semibold text-black mb-2">
                    Added Areas:
                  </p>

                  <div className="flex flex-wrap gap-3">
                    {coScholasticAreas.map(
                      (
                        area,
                        index
                      ) => (
                        <div
                          key={index}
                          className="flex items-center gap-2 px-4 py-2 rounded-lg bg-white border border-gray-300"
                        >
                          <span className="text-black font-medium">
                            {
                              area.name
                            }
                          </span>

                          <button
                            type="button"
                            onClick={() =>
                              handleRemoveCoScholasticArea(
                                index
                              )
                            }
                            className="text-red-600 font-bold hover:text-red-800"
                            title="Remove area"
                          >
                            ×
                          </button>
                        </div>
                      )
                    )}
                  </div>
                </div>
              )}

              {/* NO AREA MESSAGE */}
              {coScholasticAreas.length ===
                0 && (
                <p className="mt-4 text-sm text-gray-500">
                  No Co-Scholastic Area
                  added yet.
                </p>
              )}
            </div>

            {/* ==================================================
                CREATE BUTTON
            ================================================== */}

            <div className="flex justify-start mt-4">
              <button
                type="submit"
                style={{
                  ...buttonStyle,
                  backgroundColor:
                    loading
                      ? "#9ca3af"
                      : buttonStyle.backgroundColor,
                  cursor: loading
                    ? "not-allowed"
                    : "pointer",
                  opacity: loading
                    ? 0.8
                    : 1,
                }}
                disabled={loading}
              >
                {loading
                  ? "Please wait..."
                  : "Create Exam Datesheet"}
              </button>
            </div>
          </form>
        )}

        {/* ==================================================
            EXAM LIST
        ================================================== */}

        <div className="mt-10 text-left">

          <h3
            className="text-[18px] font-bold mb-4"
            style={{
              color:
                colors.primary,
            }}
          >
            All Created Exams
          </h3>

          {allExams.length > 0 ? (
            <ul className="space-y-4">
              {allExams.map(
                (exam) => (
                  <li
                    key={exam._id}
                    className="p-4 rounded-lg shadow-md flex justify-start items-center gap-6"
                    style={{
                      background:
                        colors.grayLight,
                    }}
                  >
                    <span className="text-[18px] font-semibold text-black">
                      {exam.class} —{" "}
                      {
                        exam.examName
                      }
                    </span>

                    <div className="flex gap-3">

                      {/* VIEW */}
                      <button
                        onClick={() =>
                          navigate(
                            `/view-datesheet/${exam._id}`,
                            {
                              state: {
                                exam,
                              },
                            }
                          )
                        }
                        className="px-4 py-2 rounded text-white"
                        style={{
                          background:
                            colors.primary,
                          fontSize:
                            "18px",
                        }}
                      >
                        View
                      </button>

                      {/* DELETE */}
                      <button
                        onClick={() =>
                          handleDelete(
                            exam._id
                          )
                        }
                        className="px-4 py-2 rounded text-white"
                        style={{
                          background:
                            "#DC2626",
                          fontSize:
                            "18px",
                        }}
                      >
                        Delete
                      </button>

                    </div>
                  </li>
                )
              )}
            </ul>
          ) : (
            <p className="text-gray-500">
              No exams found.
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default ExamDateSheet;